/**
 * Import d'un export **Dashlane JSON** (« Fichier > Exporter > Export JSON non
 * securise » des applications Dashlane de bureau, avant l'export CSV en .zip).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le fichier est un objet dont chaque cle, en MAJUSCULES,
 * nomme une categorie et porte un tableau d'objets : `AUTHENTIFIANT`
 * (identifiants : `title`, `login`, `secondaryLogin`, `email`, `password`,
 * `domain`, `note`, `otpSecret`), `PAYMENTMEANS_CREDITCARD` (`bank`, `cardNumber`, `owner`),
 * `BANKSTATEMENT` (`BankAccountName`), `IDCARD` (`Fullname`), `IDENTITY`
 * (`fullName`, `pseudo`), `ADDRESS` (`addressName`, `addressFull`, `city`,
 * `state`, `zipcode`, `country`), et d'autres (`EMAIL`, `PAYMENTMEAN_PAYPAL`...).
 * Lu comme Bitwarden le lit (`bitwarden/clients` @ fc5de8618106,
 * `importers/dashlane/dashlane-json-importer.ts`), et au-dela : Bitwarden
 * ecarte `EMAIL` et `PAYMENTMEAN_PAYPAL` et toute valeur qu'il ne nomme pas ;
 * ici toute valeur non reprise devient un champ personnalise, masque si son
 * libelle designe un secret (aucune perte).
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvFreeColumnField, joinNotes, normalizeTotpSecret, type ImportedEntry } from './csv-import.js';
import { detectCardBrand, normalizeTypedFields } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

/** Valeur brute d'une cle : chaine telle quelle, nombre en texte ; drapeaux (booleens), `null` et objets ecartes. */
function rawText(value: Json): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
}

function text(value: Json): string {
  return rawText(value).trim();
}

/** Categories dont la forme est connue ; toute autre devient une note a champs. */
const KNOWN_CATEGORIES = new Set(['ADDRESS', 'AUTHENTIFIANT', 'BANKSTATEMENT', 'IDCARD', 'IDENTITY', 'PAYMENTMEANS_CREDITCARD', 'PAYMENTMEAN_PAYPAL', 'EMAIL']);

/** Cles reprises par chaque categorie : les autres valeurs non vides deviennent des champs. */
const CONSUMED: Record<string, readonly string[]> = {
  AUTHENTIFIANT: ['title', 'login', 'secondaryLogin', 'email', 'password', 'domain', 'note', 'otpSecret', 'otpUrl'],
  ADDRESS: ['addressName', 'addressFull', 'city', 'state', 'zipcode', 'country'],
  IDENTITY: ['fullName'],
  IDCARD: ['Fullname'],
  PAYMENTMEANS_CREDITCARD: ['bank', 'cardNumber', 'owner', 'securityCode'],
  BANKSTATEMENT: ['BankAccountName', 'BankAccountOwner', 'BankAccountBank', 'BankAccountIBAN', 'BankAccountBIC'],
};

/** Valeurs non reprises : un champ chacune (non rognee sous un libelle de secret, masquee). */
function leftoverFields(item: Record<string, Json>, consumed: readonly string[]): EntryCustomField[] {
  const fields: EntryCustomField[] = [];
  for (const [key, value] of Object.entries(item)) {
    if (consumed.includes(key)) continue;
    const field = csvFreeColumnField(key, rawText(value));
    if (field !== null) fields.push(field);
  }
  return fields;
}

/** Nom de repli d'une entree : l'hote de son adresse, sans `www.`. */
function hostOf(url: string): string {
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//iu.test(url) ? url : `https://${url}`).hostname.replace(/^www\./iu, '');
  } catch {
    return url;
  }
}

/**
 * Nom complet -> prenom(s) et nom : le dernier mot est le nom, tous les autres
 * le prenom. Bitwarden ne garde que le premier mot d'un nom de quatre mots ou plus.
 */
function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.split(/\s+/u).filter(Boolean);
  if (parts.length < 2) return { firstName: parts[0] ?? '', lastName: '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1]! };
}

function base(customFields: readonly EntryCustomField[]): Omit<ImportedEntry, 'type' | 'name'> {
  return { username: '', password: '', uris: [], notes: '', totpSecret: '', favorite: false, ...(customFields.length > 0 ? { customFields } : {}) };
}

function parseCredential(item: Record<string, Json>): ImportedEntry {
  const customFields = leftoverFields(item, CONSUMED['AUTHENTIFIANT']!);
  const login = text(item['login']);
  const secondary = text(item['secondaryLogin']);
  const email = text(item['email']);
  const username = login || secondary || email;
  // L'e-mail et l'identifiant secondaire qui ne servent pas d'identifiant restent des champs (Bitwarden range l'e-mail en notes).
  if (secondary.length > 0 && secondary !== username) customFields.unshift({ name: 'Identifiant secondaire', value: secondary, hidden: false });
  if (email.length > 0 && email !== username) customFields.unshift({ name: 'E-mail', value: email, hidden: false });
  const password = rawText(item['password']);
  const domain = text(item['domain']);
  // Graine 2FA, que Bitwarden ignore ici (son lecteur CSV Dashlane la reprend sous les memes noms).
  const totp = normalizeTotpSecret(text(item['otpUrl']) || text(item['otpSecret']));
  const entry = { ...base(customFields), notes: joinNotes([rawText(item['note']).trim(), totp.extraNote]), totpSecret: totp.secret };
  // Ni identifiant, ni mot de passe, ni adresse, ni code : c'est une note securisee.
  if (username.length === 0 && password.length === 0 && domain.length === 0 && totp.secret.length === 0) {
    return { ...entry, type: 'note', name: text(item['title']) || 'Sans nom' };
  }
  return {
    ...entry,
    type: 'login',
    name: text(item['title']) || (domain.length > 0 ? hostOf(domain) : '') || username || 'Sans nom',
    username,
    password,
    uris: domain.length > 0 ? [domain] : [],
  };
}

function parseAddress(item: Record<string, Json>): ImportedEntry {
  const fields = normalizeTypedFields('identity', {
    address: text(item['addressFull']),
    city: text(item['city']),
    region: text(item['state']),
    postalCode: text(item['zipcode']),
    country: text(item['country']).toUpperCase(),
  });
  return { ...base(leftoverFields(item, CONSUMED['ADDRESS']!)), type: 'identity', name: text(item['addressName']) || fields['city'] || 'Adresse', fields };
}

function parseIdentity(item: Record<string, Json>, category: 'IDENTITY' | 'IDCARD'): ImportedEntry {
  const fullName = text(item[category === 'IDENTITY' ? 'fullName' : 'Fullname']);
  const fields = normalizeTypedFields('identity', splitFullName(fullName));
  return {
    ...base(leftoverFields(item, CONSUMED[category]!)),
    type: 'identity',
    name: fullName || (category === 'IDCARD' ? "Carte d'identite" : 'Identite'),
    fields,
  };
}

function parseCard(item: Record<string, Json>): ImportedEntry {
  const bank = text(item['bank']);
  const fields = normalizeTypedFields('card', {
    cardholder: text(item['owner']),
    number: text(item['cardNumber']),
    cvv: text(item['securityCode']),
    bank,
  });
  const brand = detectCardBrand(fields['number'] ?? '');
  return {
    ...base(leftoverFields(item, CONSUMED['PAYMENTMEANS_CREDITCARD']!)),
    type: 'card',
    name: [bank, brand].filter(Boolean).join(' - ') || 'Carte',
    fields,
  };
}

function parseBankStatement(item: Record<string, Json>): ImportedEntry {
  const fields = normalizeTypedFields('bank', {
    bankName: text(item['BankAccountBank']),
    accountHolder: text(item['BankAccountOwner']),
    iban: text(item['BankAccountIBAN']),
    bic: text(item['BankAccountBIC']),
  });
  return {
    ...base(leftoverFields(item, CONSUMED['BANKSTATEMENT']!)),
    type: 'bank',
    name: text(item['BankAccountName']) || fields['bankName'] || 'Compte bancaire',
    fields,
  };
}

/** Categorie sans forme connue (`EMAIL`, `PAYMENTMEAN_PAYPAL`, ...) : une note, chaque valeur en champ. */
function parseOther(item: Record<string, Json>, category: string): ImportedEntry {
  const nameKey = ['title', 'name', 'Name', 'Title'].find((key) => text(item[key]).length > 0);
  const name = nameKey !== undefined ? text(item[nameKey]) : category;
  return { ...base(leftoverFields(item, nameKey !== undefined ? [nameKey] : [])), type: 'note', name };
}

function parseItem(item: Record<string, Json>, category: string): ImportedEntry {
  switch (category) {
    case 'AUTHENTIFIANT':
      return parseCredential(item);
    case 'ADDRESS':
      return parseAddress(item);
    case 'IDENTITY':
    case 'IDCARD':
      return parseIdentity(item, category);
    case 'PAYMENTMEANS_CREDITCARD':
      return parseCard(item);
    case 'BANKSTATEMENT':
      return parseBankStatement(item);
    default:
      return parseOther(item, category);
  }
}

/**
 * Un export Dashlane JSON se reconnait a sa forme entiere : un objet dont
 * TOUTES les cles sont des categories en majuscules portant un tableau, dont au
 * moins une categorie connue. Une cle en minuscules (`items`, `encrypted`) en
 * fait un autre export.
 */
export function isDashlaneJsonExport(data: Json): boolean {
  const file = asObject(data);
  if (file === null) return false;
  const keys = Object.keys(file);
  return (
    keys.length > 0 &&
    keys.every((key) => /^[A-Z][A-Z_]*$/u.test(key) && Array.isArray(file[key])) &&
    keys.some((key) => KNOWN_CATEGORIES.has(key))
  );
}

/** Parse un export Dashlane JSON : chaque element de chaque categorie, dans l'ordre du fichier. */
export function parseDashlaneJsonExport(data: Json): ImportedEntry[] {
  const file = asObject(data);
  if (file === null) throw new Error('export Dashlane JSON : structure inattendue');
  const entries: ImportedEntry[] = [];
  for (const [category, items] of Object.entries(file)) {
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      const object = asObject(item);
      if (object !== null) entries.push(parseItem(object, category));
    }
  }
  return entries;
}
